import { useMemo, useEffect, useState } from 'react';
import {
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import type { WeekData } from '@/utils/week-utils';
import { getConfirmedVsPending, getCurrentWeek, getCurrentYear } from '@/utils/week-utils';
import { receiverTargetService } from '@/data/mock-receiver-targets';

interface WeeklyTargetsChartProps {
    weeklyData: WeekData[];
    receiverId: string;
    year: number;
    type: 'containers' | 'revenue';
}

export const WeeklyTargetsChart = ({ weeklyData, receiverId, year, type }: WeeklyTargetsChartProps) => {
    const { t } = useTranslation(['pages:calendar', 'common']);
    const [isDarkMode, setIsDarkMode] = useState(false);

    // Rileva dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark-mode'));
        };

        checkDarkMode();

        // Observer per cambiamenti tema
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    // Colori basati sul tema
    const colors = useMemo(() => {
        if (isDarkMode) {
            return {
                confirmed: '#10b981', // verde brillante
                pending: '#f59e0b',   // arancione
                target: '#6938ef',     // brand color (viola)
                grid: 'rgba(255, 255, 255, 0.1)',
                text: 'rgba(255, 255, 255, 0.7)',
            };
        } else {
            return {
                confirmed: '#059669', // verde scuro
                pending: '#d97706',   // arancione scuro
                target: '#dc2626',     // rosso scuro
                grid: 'rgba(0, 0, 0, 0.1)',
                text: 'rgba(0, 0, 0, 0.7)',
            };
        }
    }, [isDarkMode]);

    const chartData = useMemo(() => {
        const currentWeek = getCurrentWeek();
        const currentYear = getCurrentYear();
        const isCurrentYear = year === currentYear;

        return weeklyData.map((week) => {
            const { confirmed, pending, confirmedValue, pendingValue } = getConfirmedVsPending(week.containers);
            const target = receiverTargetService.getTarget(receiverId, year, week.weekNumber);
            const isPastWeek = isCurrentYear && week.weekNumber < currentWeek;

            // Per settimane passate: mostra solo il totale (confirmed, perché pending non ha senso)
            // Per settimane corrente e future: mostra stacked (confirmed + pending)
            const totalValue = type === 'containers'
                ? (confirmed + pending)
                : (confirmedValue + pendingValue);

            return {
                week: week.weekNumber,
                weekLabel: `W${week.weekNumber}`,
                // Per settimane passate: solo confirmed (totale)
                // Per settimane corrente/future: confirmed e pending separati per stack
                confirmed: isPastWeek
                    ? totalValue
                    : (type === 'containers' ? confirmed : confirmedValue),
                pending: isPastWeek
                    ? 0
                    : (type === 'containers' ? pending : pendingValue),
                target: type === 'containers'
                    ? (target?.containerTarget || 0)
                    : (target?.revenueTarget || 0),
                isPastWeek,
            };
        });
    }, [weeklyData, receiverId, year, type]);

    const formatValue = (value: number) => {
        if (type === 'revenue') {
            return new Intl.NumberFormat('it-IT', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(value);
        }
        return value.toString();
    };

    const yAxisLabel = type === 'containers'
        ? t('chart.yAxisContainers')
        : t('chart.yAxisRevenue');

    // Formatta valori Y-axis con K/M/B
    const formatYAxisValue = (value: number): string => {
        if (value >= 1000000000) {
            return `${(value / 1000000000).toFixed(1)}B`;
        }
        if (value >= 1000000) {
            return `${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `${(value / 1000).toFixed(0)}K`;
        }
        return value.toString();
    };

    return (
        <div className="bg-primary rounded-lg border border-secondary_alt p-6">
            <h3 className="text-lg font-semibold text-primary mb-4">
                {type === 'containers' ? t('chart.titleContainers') : t('chart.titleRevenue')}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 80, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                    <XAxis
                        dataKey="weekLabel"
                        stroke={colors.text}
                        tick={(props: any) => {
                            const currentWeek = getCurrentWeek();
                            const currentYear = getCurrentYear();
                            const weekNumber = parseInt(props.payload.value.replace('W', ''));
                            const isCurrentWeek = year === currentYear && weekNumber === currentWeek;

                            return (
                                <text
                                    x={props.x}
                                    y={props.y}
                                    fill={isCurrentWeek ? colors.target : colors.text}
                                    fontSize={11}
                                    fontWeight={isCurrentWeek ? 'bold' : 'normal'}
                                    textAnchor="end"
                                    transform={`rotate(-45, ${props.x}, ${props.y})`}
                                >
                                    {props.payload.value}
                                </text>
                            );
                        }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                        tickCount={52}
                    />
                    <YAxis
                        stroke={colors.text}
                        tick={{ fill: colors.text }}
                        tickFormatter={formatYAxisValue}
                        label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', offset: -10, style: { fill: colors.text } }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: isDarkMode ? 'rgb(19, 22, 27)' : 'rgb(255, 255, 255)',
                            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                            borderRadius: '8px',
                            color: colors.text,
                        }}
                        formatter={(value: number, name: string, props: any) => {
                            const isPastWeek = props.payload?.isPastWeek;

                            if (name === 'target') {
                                return [formatValue(value), t('chart.target')];
                            }
                            if (name === 'confirmed') {
                                // Per settimane passate, "confirmed" rappresenta il totale
                                return [formatValue(value), isPastWeek ? t('chart.total') : t('chart.confirmed')];
                            }
                            if (name === 'pending') {
                                return [formatValue(value), t('chart.pending')];
                            }
                            return [formatValue(value), name];
                        }}
                        labelFormatter={(label) => t('weekNumber', { number: label.replace('W', '') })}
                    />
                    <Legend
                        formatter={(value) => {
                            if (value === 'confirmed') return t('chart.confirmed');
                            if (value === 'pending') return t('chart.pending');
                            if (value === 'target') return t('chart.target');
                            return value;
                        }}
                        wrapperStyle={{ color: colors.text }}
                    />
                    <Bar
                        dataKey="confirmed"
                        stackId="a"
                        fill={colors.confirmed}
                        name="confirmed"
                    />
                    <Bar
                        dataKey="pending"
                        stackId="a"
                        fill={colors.pending}
                        name="pending"
                    />
                    <Line
                        type="monotone"
                        dataKey="target"
                        stroke={colors.target}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="target"
                        dot={{ fill: colors.target, r: 4 }}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

